import { jwtDecode } from "jwt-decode";

/**
 * Quién entra al portal de legales y qué parte del portal ve (módulo puro, sin React).
 *
 * Entran dos roles (ROLES_PORTAL_LEGALES):
 *  - ROLE_ADMIN: alcance EMPRESA, todo el portal. Lo que puede hacer adentro lo decide el backend
 *    (GET /api/legales/me: capacidades por rol más la tabla de autorizaciones). Ojo: un ROLE_ADMIN con una fila de
 *    autorización ASIGNADOS es abogado externo para el backend (alcance ASIGNADOS en /me): por eso el portal pide /me
 *    al abrir también para el admin y arma todo con alcanceDe(me, payload).
 *  - ROLE_ABOGADO_EXTERNO: alcance ASIGNADOS, solo sus causas. El backend le responde 403 a todo lo que está fuera
 *    de /api/legales y a las rutas "solo empresa" (PPC, estadísticas, configuración, personas…), así que el front
 *    no se las ofrece ni las pide (cada 403 dispara la alerta global del interceptor de axios).
 *
 * Precedencia (igual que LegalesPermisosResolver del backend): si el token trae ROLE_ABOGADO_EXTERNO, el usuario es
 * externo aunque también traiga ROLE_ADMIN. Los roles se comparan exactos: "ROLE_ABOGADO_EXTERNO_X" no pasa.
 *
 * El puente de sesión con la app principal (access_token ↔ legales_token, "Ir a la app") es solo para ROLE_ADMIN y
 * nunca con un token del rol externo: ver puedeUsarPuenteConLaApp y legalesAcceso.js (la solapa de la ficha).
 */

export const ROL_ADMIN = "ROLE_ADMIN";
export const ROL_ABOGADO_EXTERNO = "ROLE_ABOGADO_EXTERNO";

/** Roles que entran al portal /portal-legales. */
export const ROLES_PORTAL_LEGALES = Object.freeze([ROL_ADMIN, ROL_ABOGADO_EXTERNO]);

export const ALCANCE_EMPRESA = "EMPRESA";
export const ALCANCE_ASIGNADOS = "ASIGNADOS";

/**
 * Rutas del portal (relativas a /portal-legales) que el externo no ve: el backend le responde 403. Cada una cubre
 * también sus subrutas ("/ppc" cubre "/ppc/:id").
 */
export const RUTAS_SOLO_EMPRESA = Object.freeze(["/ppc", "/estadisticas", "/configuracion", "/personas"]);

const PREFIJO_PORTAL = "/portal-legales";

/** Login del portal: adonde el login de la app manda al abogado externo. */
export const RUTA_LOGIN_PORTAL_LEGALES = PREFIJO_PORTAL;

/** El aviso que ve el abogado externo si intenta entrar por el login de la app ("/"). */
export const AVISO_ENTRA_POR_PORTAL = "Tu usuario entra por el portal de legales.";

/**
 * Los avisos del login del portal después de un cierre de sesión: LegalesPortalLayout navega al login con
 * {salio: <aviso>} en location.state y LoginLegales, con eso, no entra solo (ni con un legales_token ni con el puente).
 */
export const AVISO_CERRASTE_SESION = "Cerraste sesión.";
export const AVISO_SESION_CERRADA = "Se cerró tu sesión. Ingresá de nuevo para seguir.";

/** La sesión de la app y la del portal en la pestaña (ver limpiarSesionesParaElPortal). */
export const CLAVES_SESION_A_LIMPIAR = Object.freeze(["access_token", "username", "legales_token", "legales_data"]);

/**
 * Borra de sessionStorage la sesión de la app (access_token, username) y la del portal (legales_token, legales_data)
 * que hubiera en la pestaña. La usan:
 *  - los logins de la app (Login, SelectCompanyPage, LoginCapacitacion) en las dos ramas, antes de guardar nada:
 *    - al abogado externo, antes de mandarlo a /portal-legales: si no, el auto-login del portal podría entrar con la
 *      sesión que dejó otra persona en esa pestaña (un legales_token vigente o el puente desde el access_token de un
 *      admin);
 *    - a cualquier otro usuario, antes de guardar su access_token: si no, el legales_token de quien usó la pestaña
 *      antes seguiría abriendo el portal con esa persona;
 *  - los cierres de sesión, a través de removeUserSession (utils/common.js): el de la app (TopNavbar, Navbar) y el del
 *    portal (LegalesPortalLayout: el botón y el vencimiento por inactividad). Cerrar sesión de un lado cierra los dos:
 *    si no, quien siga en la pestaña entra al portal como la persona que salió.
 */
export function limpiarSesionesParaElPortal() {
    try {
        CLAVES_SESION_A_LIMPIAR.forEach((k) => window.sessionStorage.removeItem(k));
    } catch { /* sin sessionStorage no hay nada que limpiar */ }
}

const CLAVES_SESION_APP = Object.freeze(["access_token", "username"]);

const usuarioDe = (payload) => (typeof payload?.sub === "string" && payload.sub.trim()
    ? payload.sub.trim().toLowerCase() : null);

/**
 * El login del portal (LoginLegales) no deja en la pestaña la sesión de la app de otra persona: borra access_token
 * y username si entra el abogado externo (su token no sirve en la app) o si el access_token es de otro usuario o no
 * se puede leer. Si es la misma persona (un admin que usa la app y el portal), la sesión de la app sigue.
 * @param {object} payload el payload del token con el que se entra al portal
 */
export function soltarSesionDeLaAppAjena(payload) {
    try {
        const deLaApp = usuarioDe(payloadDe(window.sessionStorage.getItem("access_token")));
        if (esRolExterno(payload) || !deLaApp || deLaApp !== usuarioDe(payload)) {
            CLAVES_SESION_APP.forEach((k) => window.sessionStorage.removeItem(k));
        }
    } catch { /* sin sessionStorage no hay nada que soltar */ }
}

/**
 * Los roles como lista de strings, vengan como vengan:
 *  - el payload del JWT ({roles: [{authority: "ROLE_ADMIN"}]} o {roles: ["ROLE_ADMIN"]}),
 *  - la lista de roles en cualquiera de esos dos formatos,
 *  - un string ("ROLE_ADMIN" o "ROLE_RRHH,ROLE_ADMIN", como el getRol de NewContext).
 * Lo que no es un rol reconocible se descarta. No cambia mayúsculas: la comparación es exacta, como en el backend.
 * @param {object|Array|string|null|undefined} payloadOroles
 * @returns {string[]}
 */
export function rolesDe(payloadOroles) {
    const x = payloadOroles;
    if (Array.isArray(x)) {
        return x
            .map((r) => (typeof r === "string" ? r : r && typeof r.authority === "string" ? r.authority : null))
            .filter((r) => typeof r === "string" && r.length > 0);
    }
    if (typeof x === "string") return x.split(",").map((r) => r.trim()).filter(Boolean);
    if (x && typeof x === "object") return Array.isArray(x.roles) ? rolesDe(x.roles) : [];
    return [];
}

/** ¿Trae el rol del abogado externo? Gana sobre ROLE_ADMIN. */
export function esRolExterno(payloadOroles) {
    return rolesDe(payloadOroles).includes(ROL_ABOGADO_EXTERNO);
}

/** ¿Puede entrar al portal de legales (ROLE_ADMIN o ROLE_ABOGADO_EXTERNO)? No mira el vencimiento: ver tokenVigente. */
export function puedeEntrarAlPortal(payloadOroles) {
    return rolesDe(payloadOroles).some((r) => ROLES_PORTAL_LEGALES.includes(r));
}

/**
 * ¿Sirve para el puente con la app principal (access_token → legales_token y "Ir a la app")? Solo ROLE_ADMIN y
 * nunca con el rol externo, aunque el token traiga los dos.
 */
export function puedeUsarPuenteConLaApp(payloadOroles) {
    const roles = rolesDe(payloadOroles);
    return roles.includes(ROL_ADMIN) && !roles.includes(ROL_ABOGADO_EXTERNO);
}

/** El payload de un JWT, o null si no se puede decodificar. */
export function payloadDe(token) {
    if (!token || typeof token !== "string") return null;
    try {
        const p = jwtDecode(token);
        return p && typeof p === "object" ? p : null;
    } catch {
        return null;
    }
}

/** ¿El payload tiene un exp numérico y todavía no venció? (con `margenSeg` de anticipación) */
export function tokenVigente(payload, margenSeg = 0) {
    const exp = Number(payload?.exp);
    return Number.isFinite(exp) && exp > Date.now() / 1000 + margenSeg;
}

/** El payload del legales_token de la sesión (null si no hay o no se puede leer). */
export function payloadDeLaSesionLegales() {
    try {
        return payloadDe(window.sessionStorage.getItem("legales_token"));
    } catch {
        return null;
    }
}

/**
 * El alcance del usuario en el portal: "EMPRESA" (todas las causas) o "ASIGNADOS" (solo las suyas).
 *  - Si el token trae ROLE_ABOGADO_EXTERNO: siempre ASIGNADOS (diga lo que diga /me).
 *  - Si /me ya llegó y trae alcance: ese. alcance null en /me es un externo todavía sin habilitar (el backend también
 *    lo da así a una fila ASIGNADOS inválida): se trata como ASIGNADOS, que cierra ante la duda.
 *  - Mientras /me no llegó (o un backend viejo sin el campo): EMPRESA salvo que el token sea del externo.
 * @param {import("../../../api/legales.api").Me|null|undefined} me
 * @param {object|Array|string|null|undefined} payload  El payload del token (o sus roles).
 * @returns {"EMPRESA"|"ASIGNADOS"}
 */
export function alcanceDe(me, payload) {
    if (esRolExterno(payload)) return ALCANCE_ASIGNADOS;
    if (me && typeof me === "object" && "alcance" in me) {
        if (me.alcance === ALCANCE_EMPRESA) return ALCANCE_EMPRESA;
        return ALCANCE_ASIGNADOS;
    }
    return ALCANCE_EMPRESA;
}

/** ¿El alcance es el de un abogado externo (solo sus causas)? Cualquier valor que no sea EMPRESA cuenta como tal. */
export function esAlcanceAsignados(alcance) {
    return alcance !== ALCANCE_EMPRESA;
}

/** ¿/me dice que puede leer? Un externo sin habilitar recibe 200 con capacidades.leer = false. */
export function estaHabilitado(me) {
    return me?.capacidades?.leer === true;
}

/**
 * La ruta relativa al portal, normalizada para comparar: sin el prefijo /portal-legales, sin query ni hash, sin
 * barras repetidas ni la final, decodificada y en minúsculas (react-router v6 compara rutas sin distinguir
 * mayúsculas: "/PPC" abre la ruta "ppc").
 */
export function rutaRelativa(ruta) {
    let r = String(ruta ?? "");
    r = r.split(/[?#]/)[0];
    try {
        r = decodeURIComponent(r);
    } catch { /* se compara tal cual */ }
    r = `/${r}`.replace(/\/{2,}/g, "/").toLowerCase();
    if (r === PREFIJO_PORTAL || r.startsWith(`${PREFIJO_PORTAL}/`)) r = r.slice(PREFIJO_PORTAL.length) || "/";
    if (r.length > 1) r = r.replace(/\/+$/, "");
    return r || "/";
}

/**
 * ¿Puede abrir esta ruta del portal? Con EMPRESA, todas; con ASIGNADOS, todas menos RUTAS_SOLO_EMPRESA y sus
 * subrutas. Acepta la ruta relativa ("/ppc/1") o la completa ("/portal-legales/ppc/1").
 * @param {"EMPRESA"|"ASIGNADOS"} alcance
 * @param {string} ruta
 */
export function rutaPermitida(alcance, ruta) {
    if (!esAlcanceAsignados(alcance)) return true;
    const r = rutaRelativa(ruta);
    return !RUTAS_SOLO_EMPRESA.some((p) => r === p || r.startsWith(`${p}/`));
}

/**
 * Las secciones del menú ([{title, items: [{key, label, path, Icon}]}]) sin los items que el alcance no puede abrir.
 * Las secciones que quedan vacías se sacan. No muta la entrada.
 */
export function filtrarSecciones(alcance, secciones) {
    const lista = Array.isArray(secciones) ? secciones : [];
    if (!esAlcanceAsignados(alcance)) return lista;
    return lista
        .map((s) => ({ ...s, items: (s.items || []).filter((it) => rutaPermitida(alcance, it.path)) }))
        .filter((s) => s.items.length > 0);
}

/**
 * ¿Puede modificar este registro (caso, hilo, audiencia, acuerdo)?
 *  - ASIGNADOS (me.alcance, o sin me y el token del externo): solo si el registro trae mi_permiso === "GESTIONA".
 *  - EMPRESA: me.capacidades.escribir (mi_permiso viene en null).
 * Sin /me: false.
 * @param {import("../../../api/legales.api").Me|null|undefined} me
 * @param {{mi_permiso?: ("VE"|"GESTIONA"|null)}|null|undefined} item
 * @param {object} [payload]  El payload del token; por defecto, el del legales_token de la sesión.
 */
export function puedeGestionar(me, item, payload = payloadDeLaSesionLegales()) {
    if (!me || typeof me !== "object") return false;
    if (esAlcanceAsignados(alcanceDe(me, payload))) return item?.mi_permiso === "GESTIONA";
    return me.capacidades?.escribir === true;
}
